<template>
    <TransitionRoot appear
                    :show="isOpen"
                    as="template">
        <Dialog :open="isOpen"
                :initial-focus="initialFocusElement"
                class="overflow-y-auto fixed inset-y-0 left-0 z-dialog w-screen"
                @close="closeModal">
            <div class="flex overflow-hidden relative h-screen"
                 :class="{'flex-col': isTopOrBottomPosition}">
                <TransitionChild v-if="showBackdrop"
                                 as="template"
                                 enter="duration-300 ease-out"
                                 enter-from="opacity-0"
                                 enter-to="opacity-100"
                                 leave="duration-200 ease-in"
                                 leave-from="opacity-100"
                                 leave-to="opacity-0">
                    <DialogOverlay class="fixed inset-y-0 left-0 w-screen bg-black/60"/>
                </TransitionChild>

                <TransitionChild as="template"
                                 enter="duration-300 ease-out"
                                 leave="duration-200 ease-in"
                                 v-bind="drawerAnimation"
                                 @after-enter="onDialogOpen">
                    <div class="flex flex-col pt-30 pr-15 pl-25 md:px-50 md:pt-50"
                         :class="drawerPositionStyling">
                        <DialogTitle v-if="title"
                                     v-autofocus="autofocus" 
                                     tabindex="-1"
                                     class="flex justify-between items-center mb-10 text-16 md:text-24"
                                     :class="titleClasses"
                                     as="h4">
                            {{ title }}
                        </DialogTitle>
                        <button type="button"
                                :aria-label="$translate('Accessibility.Minibasket.CloseBasket')"
                                class="absolute top-0 right-0 mt-30 mr-20 h-30 bg-white rounded-full transition-transform hover:rotate-180 md:mt-50 md:mr-40"
                                @click="closeModal()">
                            <CIcon name="close-content"
                                   color="black"
                                   class="w-30 h-30"
                                   :fill="false"/>
                        </button>
                        <DialogDescription v-if="description">
                            {{ description }}
                        </DialogDescription>
                        <slot :close="closeModal"/>

                        <svg v-if="position === 'right' || position === 'left'"
                             width="504px"
                             height="1158px"
                             viewBox="0 0 504 1158"
                             version="1.1"
                             preserveAspectRatio="none"
                             class="absolute inset-0 -z-2 w-full h-screen"
                             :class="{'rotate-180': position === 'left'}">
                            <defs>
                                <rect id="path-FEF3BB8F"
                                      x="0"
                                      y="0"
                                      width="504"
                                      height="1157"/>
                                <path id="path-8AEC"
                                      d="M7.25968419,-8.9987112 L603,-8.9987112 L603,1191 L7.25968419,1191 C9.09832436,1170.18232 10.1490127,1155.34662 10.4117494,1146.49292 C11.1301796,1122.28324 9.87025725,1103.30042 10.4117494,1090.63928 C11.5622908,1063.73739 14.8214999,1033.73209 14.8214999,1018.61508 C14.8214999,1004.73322 8.94233876,999.283706 8.77121691,990.277115 C7.91710788,945.32312 10.4117494,930.81387 10.4117494,889.32514 C10.4117494,846.963799 10.1166941,815.622073 10.4117494,795.345094 C11.0066683,754.460683 14.741502,724.191711 14.8214999,709.866674 C14.9206325,692.115237 10.4117494,681.774748 10.4117494,678.866659 C10.4117494,628.62298 7.17012512,589.870295 7.25968419,568.281431 C7.41877456,529.93154 10.4117494,506.006104 10.4117494,498.454645 C10.4117494,458.943792 5.62875362,432.992684 5.62875456,423.988743 C5.62875456,380.210906 10.4117494,356.780165 10.4117494,352.612673 C10.4117494,327.203345 8.77121691,312.916008 8.77121691,309.082717 C8.77121691,289.819055 10.4264916,275.363255 10.4117494,265.54857 C10.3783367,243.304041 5.62875362,230.543377 5.62875456,226.710212 C5.62875456,206.266114 7.26063099,190.07035 7.25968419,178.116383 C7.25793334,156.010726 7.26211076,138.137127 7.25968419,126.565997 C7.25937316,125.08286 5.62891915,122.970696 5.62875456,121.552779 C5.62732256,109.216187 7.26132581,97.5294597 7.25968419,86.8267272 C7.25820939,77.2116021 6.13367425,67.1209962 5.62875456,57.9063774 C3.00680108,10.0565855 3.55044429,-12.2451107 7.25968419,-8.9987112 Z"/>
                            </defs>
                            <g id="Checkout-v2"
                               stroke="none"
                               stroke-width="1"
                               fill="none"
                               fill-rule="evenodd">
                                <g id="FEF3BB8F"
                                   transform="translate(-293.000000, -1187.000000)">
                                    <g id="minibasket-complete-bg"
                                       transform="translate(293.000000, 1187.308101)">
                                        <mask id="mask-A9E7"
                                              fill="white">
                                            <use xlink:href="#path-FEF3BB8F"/>
                                        </mask>
                                        <g id="Rectangle"/>
                                        <g id="Rectangle"
                                           mask="url(#mask-A9E7)">
                                            <use vector-effect="non-scaling-stroke"
                                                 fill="#FFFFFF"
                                                 fill-rule="evenodd"
                                                 xlink:href="#path-8AEC"/>
                                            <use vector-effect="non-scaling-stroke"
                                                 stroke="#FFFFFF"
                                                 stroke-width="5"
                                                 xlink:href="#path-8AEC"/>
                                            <path vector-effect="non-scaling-stroke"
                                                  stroke="#000000"
                                                  stroke-width="6"
                                                  d="M600,-5.9987112 L600,1188 L10.534124,1188 C12.2030091,1168.80661 13.1606039,1155.00053 13.4104293,1146.58191 C13.8072643,1133.20935 13.6037055,1121.42515 13.4300709,1111.41232 C13.2898857,1103.32839 13.1675264,1096.41381 13.4090095,1090.76747 C13.6061191,1086.15867 13.8654333,1081.45839 14.159456,1076.76058 L14.159456,1076.76058 L14.2716674,1074.99323 C14.2905998,1074.69913 14.3096556,1074.40507 14.3286715,1074.11346 L14.3286715,1074.11346 L14.4452296,1072.34778 C14.5433478,1070.87875 14.6440523,1069.41248 14.7463911,1067.95345 L14.7463911,1067.95345 L14.8702944,1066.20109 C15.1913475,1061.69506 15.5259982,1057.25287 15.8496178,1052.957 C16.894874,1039.08182 17.8214999,1026.72166 17.8214999,1018.61508 C17.8214999,1010.38574 15.8927861,1004.9521 14.1430307,1000.09136 C12.9612745,996.808499 11.8393272,993.833443 11.7706756,990.220126 C11.3787623,969.592705 11.6943455,955.395813 12.148936,942.046908 L12.1964352,940.668782 C12.2204094,939.981003 12.2447031,939.294978 12.2692375,938.609934 L12.2692375,938.609934 L12.3317019,936.880433 C12.3526599,936.304403 12.373742,935.728606 12.3948922,935.152832 L12.3948922,935.152832 L12.4585888,933.422721 C12.9209252,920.880799 13.3898316,908.151355 13.4110068,890.56208 L13.4110068,890.56208 L13.4114023,886.795864 C13.4074089,872.173056 13.3696774,858.890931 13.3357575,846.951402 C13.3084167,837.327644 13.2835515,828.576594 13.2808555,820.700182 L13.2808555,820.700182 L13.280773,818.209485 C13.2833364,809.472645 13.3171907,801.865239 13.4114318,795.388743 C13.7741268,770.463381 15.3081599,749.4948 16.4631451,733.697177 C17.2056732,723.541048 17.7900991,715.497927 17.8214531,709.883427 C17.8942564,696.846722 15.5569415,687.695164 14.2106545,682.439077 C13.7769181,680.745711 13.4117494,679.557584 13.4117494,678.866659 C13.4117494,649.005598 12.2693676,623.196323 11.3583484,602.625613 C10.7375431,588.607897 10.2233871,577.037324 10.2596584,568.293876 C10.368475,542.062832 11.8085834,522.604656 12.7005072,510.54765 C13.1169766,504.917827 13.4117494,500.86635 13.4117494,498.454645 C13.4117494,471.570113 11.210865,450.930627 9.78724128,437.592501 C9.12665705,431.403397 8.62875427,426.831808 8.62875456,423.988743 C8.62875456,390.730949 11.4050566,369.28595 12.7145591,359.172978 C13.1463496,355.838364 13.4117494,353.656279 13.4117494,352.612673 C13.4117494,334.213342 12.5563726,321.614612 12.0776562,314.565597 C11.8993232,311.939676 11.7712169,310.116804 11.7712169,309.082717 C11.7712169,297.969881 12.3265782,288.466896 12.788812,280.541279 C13.1290806,274.706919 13.4180064,269.711966 13.411746,265.544064 C13.3877112,249.542868 10.9730194,238.356855 9.55242192,231.787868 C9.03616305,229.400635 8.62875432,227.714015 8.62875456,226.710212 C8.62875456,215.186833 9.15078218,205.019998 9.60238352,196.208468 C9.95269215,189.373335 10.2600981,183.342716 10.2596842,178.11641 L10.2596842,178.11641 L10.2599616,147.991335 L10.2600092,147.197622 L10.26006,128.772896 L10.2599792,128.207687 L10.2596841,126.565368 C10.2594535,125.465461 9.7713139,124.095596 9.15826478,122.75636 C8.96038065,122.324072 8.62879629,121.912067 8.62875454,121.55243 C8.62802163,115.238429 9.06294459,109.096725 9.48017231,103.17298 C9.87850568,97.517495 10.2604863,92.0558702 10.2596842,86.8262671 C10.2586957,80.3821475 9.76190015,73.7267527 9.27622556,67.2578896 C9.03322469,64.0212794 8.793577,60.8322011 8.62426086,57.7422371 C7.31919774,33.9252629 6.79853406,16.4664756 7.06913091,5.36586993 C7.1983772,0.0638422287 7.49352857,-3.72377587 7.99926576,-5.98976153 L7.99926576,-5.98976153 L600,-5.9987112 Z"
                                                  stroke-linecap="square"/>
                                        </g>
                                    </g>
                                </g>
                            </g>
                        </svg>
                    </div>
                </TransitionChild>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import { Dialog, DialogDescription, DialogOverlay, DialogTitle, TransitionRoot, TransitionChild } from '@headlessui/vue';
import bus from '@/core/bus';
import { CloseDialogKey, DialogClosedKey } from '@/project/dialog/dialog';

export default defineComponent({
    name: 'DrawerDialog',
    components: { Dialog, DialogOverlay, DialogTitle, DialogDescription, TransitionRoot, TransitionChild },
    props: {
        title: {
            type: String,
            default: '',
        },
        position: {
            type: String,
            required: true,
            validator: (value: string) => ['top', 'right', 'bottom', 'left'].includes(value),
        },
        useSlideAnimation: {
            type: Boolean,
            required: false,
            default: false,
        },
        showBackdrop: {
            type: Boolean,
            required: false,
            default: true,
        },
        description: {
            type: String,
            default: '',
        },
        initialFocusElement: {
            type: Object as PropType<HTMLElement | undefined>,
            required: false,
            default: undefined,
        },
        titleClasses: {
            type: String,
            required: false,
            default: '',
        }, 
        autofocus: {
            type: Boolean,
            required: false,
            default: true,
        },
    },
    emits: ['visible'],
    setup(props, { emit }) {
        const isOpen = ref(true);

        function getDrawerStyling(): string {
            switch (props.position) {
            case 'top':
                return 'w-full bg-white z-1';
            case 'right':
                return 'fixed top-0 bottom-0 right-0 w-full z-1 w-320 md:w-500';
            case 'bottom':
                return 'mt-auto mb-0 w-full bg-white z-1';
            case 'left':
                return 'fixed top-0 bottom-0 left-0 w-full z-1 w-320 md:w-500';
            default:
                return '';
            }
        }

        const drawerPositionStyling: string = getDrawerStyling();
        const isTopOrBottomPosition: boolean = props.position === 'top' || props.position === 'bottom';

        function getDrawerAnimation(): Record<string, string> {
            if (props.useSlideAnimation) {
                switch (props.position) {
                case 'top':
                    return {
                        enterFrom: 'opacity-0 -translate-y-full',
                        enterTo: 'opacity-100 translate-y-0',
                        leaveFrom: 'opacity-100 translate-y-0',
                        leaveTo: 'opacity-0 -translate-y-full',
                    };
                case 'right':
                    return {
                        enterFrom: 'opacity-0 translate-x-full absolute',
                        enterTo: 'opacity-100 translate-x-0',
                        leaveFrom: 'opacity-100 translate-x-0',
                        leaveTo: 'opacity-0 translate-x-full',
                    };
                case 'bottom':
                    return {
                        enterFrom: 'opacity-0 translate-y-full absolute',
                        enterTo: 'opacity-100 translate-y-0',
                        leaveFrom: 'opacity-100 translate-y-0',
                        leaveTo: 'opacity-0 translate-y-full',
                    };
                case 'left':
                    return {
                        enterFrom: 'opacity-0 -translate-x-full',
                        enterTo: 'opacity-100 translate-x-0',
                        leaveFrom: 'opacity-100 translate-x-0',
                        leaveTo: 'opacity-0 -translate-x-full',
                    };
                }
            }
            return {
                enterFrom: 'opacity-0',
                enterTo: 'opacity-100',
                leaveFrom: 'opacity-100',
                leaveTo: 'opacity-0',
            };
        }

        const drawerAnimation = getDrawerAnimation();

        bus.on(CloseDialogKey, () => {
            closeModal();
        });

        function closeModal() {
            isOpen.value = false;
            bus.emit(DialogClosedKey);
        } 

        return {
            isOpen,
            closeModal,
            onDialogOpen(): void {
                emit('visible');
            },
            drawerPositionStyling,
            isTopOrBottomPosition,
            drawerAnimation,
        };
    },
});
</script>
